import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import Register from './pages/Auth/Register';
import Login from './pages/Auth/Login';
import UserAccount from './pages/Dashboard/UserAccount';
import TrainerDashboard from './pages/Dashboard/TrainerDashboard';
import ResetPassword from './pages/Auth/ResetPassword';
import CourseCalendar from './pages/Dashboard/CourseCalendar';
import DBConnectionTest from './DBConnectionTest';
import TestEmail from './pages/TestEmail';
import TestReceiptUpload from './pages/TestReceiptUpload';
import AdminDashboard from './pages/Dashboard/AdminDashboard';
import MyRegistrations from './pages/Dashboard/MyRegistrations';
import MyCertificates from './pages/Dashboard/MyCertificates';

import FirstLoginResetModal from './components/FirstLoginResetModal';
import RegistrationPending from './pages/Auth/RegistrationPending';
import FeedbackModal, { FloatingFeedbackButton } from './components/FeedbackModal';

function App() {
  const [currentView, setCurrentView] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    if (viewParam) {
      if (viewParam === 'dashboard') {
        const saved = localStorage.getItem('user');
        let role = '';
        try {
          const u = saved ? JSON.parse(saved) : null;
          role = (u?.role || u?.Role || '').trim().toLowerCase();
        } catch {}
        if (role === 'admin' || role === 'editor') return 'adminDashboard';
        if (role === 'trainer') return 'trainerDashboard';
        if (role === 'report viewer' || role === 'reportviewer' || role === 'viewer') return 'adminReports';
        return 'calendar';
      }
      return viewParam;
    }

    const path = window.location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
    if (path === 'register') return 'register';
    if (path === 'registration-pending' || path === 'registrationpending') return 'registrationPending';
    if (path === 'login') return 'login';
    if (path === 'reset-password' || path === 'resetpassword') return 'resetPassword';
    if (path === 'account' || path === 'profile') return 'account';
    if (path === 'calendar') return 'calendar';
    if (path === 'dashboard') {
      const saved = localStorage.getItem('user');
      let role = '';
      try {
        const u = saved ? JSON.parse(saved) : null;
        role = (u?.role || u?.Role || '').trim().toLowerCase();
      } catch {}
      if (role === 'admin' || role === 'editor') return 'adminDashboard';
      if (role === 'trainer') return 'trainerDashboard';
      if (role === 'report viewer' || role === 'reportviewer' || role === 'viewer') return 'adminReports';
      return 'calendar';
    }
    if (path === 'trainer-calendar' || path === 'trainercalendar') return 'trainerCalendar';
    if (path === 'trainer-profile' || path === 'trainerprofile') return 'trainerProfile';
    if (path === 'trainer-dashboard' || path === 'trainerdashboard' || path === 'trainer') return 'trainerDashboard';
    if (path === 'admin' || path === 'admindashboard') return 'adminDashboard';
    if (path === 'account-registration' || path === 'accountregistration' || path === 'adminaccountregistration') return 'adminAccountRegistration';
    if (path === 'reports' || path === 'adminreports') return 'adminReports';
    if (path === 'audit-logs' || path === 'auditlogs' || path === 'adminauditlogs') return 'adminAuditLogs';
    if (path === 'system-settings' || path === 'systemsettings' || path === 'adminsystemsettings' || path === 'application-settings' || path === 'applicationsettings' || path === 'adminapplicationsettings') return 'adminSystemSettings';
    if (path === 'feedback' || path === 'adminfeedback' || path === 'admin-feedback') return 'adminFeedback';
    if (path === 'import-registrations' || path === 'bulk-registrations' || path === 'adminregistrationbulkimport') return 'adminRegistrationBulkImport';
    if (path === 'my-registrations' || path === 'myregistrations') return 'myRegistrations';
    if (path === 'my-certificates' || path === 'mycertificates') return 'myCertificates';
    if (path === 'db-test' || path === 'dbtest') return 'dbTest';
    if (path === 'testemail') return 'testEmail';
    if (path === 'testreceipts') return 'testReceipts';

    // Default route for "/" is login
    return 'login';
  });
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const updateUserSession = (newUserData) => {
    setUser(newUserData);
    try {
      if (newUserData) {
        localStorage.setItem('user', JSON.stringify(newUserData));
      } else {
        localStorage.removeItem('user');
      }
    } catch (e) {
      console.error('Failed to update user session storage', e);
    }
  };
  const [pendingRegistrationData, setPendingRegistrationData] = useState(null);
  const [isDevNavExpanded, setIsDevNavExpanded] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  // Tracks whether the auto-trigger has already fired for the current view
  const hasAutoTriggeredFeedback = useRef(false);

  // Auto-pop out feedback form is ONLY enabled for Trainer Dashboard and Admin Report page
  const isTrainerDashboard = Boolean(
    (user?.role?.toLowerCase() === 'trainer' && (currentView === 'account' || (typeof currentView === 'string' && currentView.toLowerCase().startsWith('trainer')))) ||
    (typeof currentView === 'string' && currentView.toLowerCase().startsWith('trainer'))
  );

  const isAdminReportPage = Boolean(
    currentView === 'adminReports' ||
    currentView === 'reports' ||
    currentView === 'adminreports' ||
    currentView === 'adminReport'
  );

  const isAutoFeedbackAllowed = isTrainerDashboard || isAdminReportPage;

  // Reset auto-trigger flag whenever the user navigates to a different view
  // and ensure the feedback modal is closed on pages where auto feedback is not allowed
  useEffect(() => {
    hasAutoTriggeredFeedback.current = false;
    if (!isAutoFeedbackAllowed) {
      setIsFeedbackModalOpen(false);
    }
  }, [currentView, isAutoFeedbackAllowed]);

  // Auto-open feedback modal when user scrolls past 50% of the page ONLY for trainer dashboard and Admin Report page
  const handleScrollAutoTrigger = useCallback(() => {
    if (!isAutoFeedbackAllowed || hasAutoTriggeredFeedback.current || isFeedbackModalOpen) return;

    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (docHeight <= 0) return;

    const scrollPercent = (scrollTop / docHeight) * 100;
    if (scrollPercent >= 50) {
      hasAutoTriggeredFeedback.current = true;
      setIsFeedbackModalOpen(true);
    }
  }, [isAutoFeedbackAllowed, isFeedbackModalOpen]);

  useEffect(() => {
    if (!isAutoFeedbackAllowed) return;

    window.addEventListener('scroll', handleScrollAutoTrigger, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollAutoTrigger);
  }, [isAutoFeedbackAllowed, handleScrollAutoTrigger]);

  const getViewDisplayName = (view) => {
    switch (view) {
      case 'register': return 'Learner Registration Form';
      case 'registrationPending': return 'Civil Service Registration Pending';
      case 'login': return 'Login';
      case 'resetPassword': return 'Reset Password';
      case 'account': return 'User Account';
      case 'dashboard':
      case 'calendar': return 'Course Calendar';
      case 'trainerCalendar': return 'Training Calendar';
      case 'trainerProfile': return 'Trainer Profile';
      case 'trainerDashboard': return 'Trainer Dashboard';
      case 'adminDashboard': return 'Admin Dashboard';
      case 'adminAccountRegistration': return 'Account Registration';
      case 'adminReports': return 'Training & Class Reports';
      case 'adminAuditLogs': return 'Audit Logs';
      case 'adminSystemSettings': return 'Application Settings';
      case 'adminFeedback': return 'Feedback Management';
      case 'adminRegistrationBulkImport': return 'Import Registrations';
      case 'myRegistrations': return 'My Registrations';
      case 'myCertificates': return 'My Certificates';
      case 'dbTest': return 'Database Connection Test';
      case 'testEmail': return 'Test Email';
      case 'testReceipts': return 'Receipt S3 Test';
      default: return view;
    }
  };

  const getViewPath = (view) => {
    switch (view) {
      case 'register': return '/register';
      case 'registrationPending': return '/registration-pending';
      case 'login': return '/login';
      case 'resetPassword': return '/reset-password';
      case 'account': return '/account';
      case 'trainerCalendar': return '/trainer-calendar';
      case 'trainerProfile': return '/trainer-profile';
      case 'trainerDashboard': return '/trainer-dashboard';
      case 'dashboard':
      case 'calendar': return '/calendar';
      case 'adminDashboard': return '/admin';
      case 'adminAccountRegistration': return '/account-registration';
      case 'adminReports': return '/reports';
      case 'adminAuditLogs': return '/audit-logs';
      case 'adminSystemSettings': return '/system-settings';
      case 'adminFeedback': return '/feedback';
      case 'adminRegistrationBulkImport': return '/import-registrations';
      case 'myRegistrations': return '/my-registrations';
      case 'myCertificates': return '/my-certificates';
      case 'dbTest': return '/db-test';
      case 'testEmail': return '/testemail';
      case 'testReceipts': return '/testreceipts';
      default: return `/${view}`;
    }
  };

  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const viewParam = params.get('view');
      if (viewParam) {
        if (viewParam === 'dashboard') {
          const saved = localStorage.getItem('user');
          let role = '';
          try {
            const u = saved ? JSON.parse(saved) : null;
            role = (u?.role || u?.Role || '').trim().toLowerCase();
          } catch {}
          if (role === 'admin' || role === 'editor') setCurrentView('adminDashboard');
          else if (role === 'trainer') setCurrentView('trainerDashboard');
          else if (role === 'report viewer' || role === 'reportviewer' || role === 'viewer') setCurrentView('adminReports');
          else setCurrentView('calendar');
          return;
        }
        setCurrentView(viewParam);
        return;
      }
      const path = window.location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
      if (path === 'register') setCurrentView('register');
      else if (path === 'login') setCurrentView('login');
      else if (path === 'reset-password' || path === 'resetpassword') setCurrentView('resetPassword');
      else if (path === 'account' || path === 'profile') setCurrentView('account');
      else if (path === 'calendar') setCurrentView('calendar');
      else if (path === 'dashboard') {
        const saved = localStorage.getItem('user');
        let role = '';
        try {
          const u = saved ? JSON.parse(saved) : null;
          role = (u?.role || u?.Role || '').trim().toLowerCase();
        } catch {}
        if (role === 'admin' || role === 'editor') setCurrentView('adminDashboard');
        else if (role === 'trainer') setCurrentView('trainerDashboard');
        else if (role === 'report viewer' || role === 'reportviewer' || role === 'viewer') setCurrentView('adminReports');
        else setCurrentView('calendar');
      }
      else if (path === 'admin' || path === 'admindashboard') setCurrentView('adminDashboard');
      else if (path === 'reports' || path === 'adminreports') setCurrentView('adminReports');
      else if (path === 'audit-logs' || path === 'auditlogs' || path === 'adminauditlogs') setCurrentView('adminAuditLogs');
      else if (path === 'system-settings' || path === 'systemsettings' || path === 'adminsystemsettings' || path === 'application-settings' || path === 'applicationsettings' || path === 'adminapplicationsettings') setCurrentView('adminSystemSettings');
      else if (path === 'feedback' || path === 'adminfeedback' || path === 'admin-feedback') setCurrentView('adminFeedback');
      else if (path === 'import-registrations' || path === 'bulk-registrations' || path === 'adminregistrationbulkimport') setCurrentView('adminRegistrationBulkImport');
      else if (path === 'my-registrations' || path === 'myregistrations') setCurrentView('myRegistrations');
      else if (path === 'testemail') setCurrentView('testEmail');
      else if (path === 'testreceipts') setCurrentView('testReceipts');
      else if (!path) setCurrentView('login');
    };
    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  // Always reset scroll to top when switching views
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentView]);

  // Synchronize browser URL pathname with active currentView and notify Hotjar
  useEffect(() => {
    const targetPath = getViewPath(currentView);
    if (window.location.pathname !== targetPath && !targetPath.includes('?')) {
      window.history.replaceState({}, '', targetPath);
    }
    // Track client-side SPA view transitions in Hotjar
    if (typeof window !== 'undefined' && typeof window.hj === 'function') {
      window.hj('stateChange', targetPath);
    }
  }, [currentView]);

  const userRole = (user?.role || user?.Role || '').trim().toLowerCase();
  const isReportViewer = userRole === 'report viewer' || userRole === 'reportviewer' || userRole === 'viewer';

  // Guard views for Report Viewer
  useEffect(() => {
    if (isReportViewer) {
      if (currentView !== 'adminReports' && currentView !== 'account' && currentView !== 'login') {
        setCurrentView('adminReports');
      }
    }
  }, [isReportViewer, currentView]);

  return (
    <div className="App">
      {/* Expandable & Retractable Development Navigation Bar */}
      <div className="dev-nav-bar">
        <div className="dev-nav-header">
          <div className="dev-nav-left">
            <span className="dev-nav-badge">Dev Menu</span>
            <span className="dev-nav-active-view">
              <span className="dev-nav-active-indicator"></span>
              {getViewDisplayName(currentView)}
            </span>
            <a
              href={getViewPath(currentView)}
              className="dev-nav-path-link"
              onClick={(e) => {
                e.preventDefault();
                const path = getViewPath(currentView);
                if (window.location.pathname !== path) {
                  window.history.pushState({}, '', path);
                }
              }}
              title={`Page path: ${getViewPath(currentView)} (Click to copy/sync address)`}
            >
              <svg className="dev-nav-path-icon" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <span className="dev-nav-path-text">{getViewPath(currentView)}</span>
            </a>
          </div>
          <button
            type="button"
            className="dev-nav-toggle-btn"
            onClick={() => setIsDevNavExpanded(!isDevNavExpanded)}
            aria-expanded={isDevNavExpanded}
            aria-label={isDevNavExpanded ? 'Retract development navigation menu' : 'Expand development navigation menu'}
          >
            <span>{isDevNavExpanded ? 'Retract' : 'Expand'}</span>
            <span className={`dev-nav-toggle-icon ${isDevNavExpanded ? 'open' : ''}`}>
              {isDevNavExpanded ? '▲' : '▼'}
            </span>
          </button>
        </div>

        {isDevNavExpanded && (
          <div className="dev-nav-drawer">
            <div className="dev-nav-items">
              <button
                type="button"
                className={`dev-nav-item-btn ${currentView === 'register' ? 'active' : ''}`}
                onClick={() => setCurrentView('register')}
              >
                <span>Learner Registration Form</span>
                <span className="dev-nav-item-path">/register</span>
              </button>
              <button
                type="button"
                className={`dev-nav-item-btn ${currentView === 'login' ? 'active' : ''}`}
                onClick={() => setCurrentView('login')}
              >
                <span>Login</span>
                <span className="dev-nav-item-path">/login</span>
              </button>
              <button
                type="button"
                className={`dev-nav-item-btn ${currentView === 'resetPassword' ? 'active' : ''}`}
                onClick={() => setCurrentView('resetPassword')}
              >
                <span>Reset Password</span>
                <span className="dev-nav-item-path">/reset-password</span>
              </button>
              <button
                type="button"
                className={`dev-nav-item-btn ${currentView === 'account' ? 'active' : ''}`}
                onClick={() => setCurrentView('account')}
              >
                <span>User Account</span>
                <span className="dev-nav-item-path">/account</span>
              </button>
              <button
                type="button"
                className={`dev-nav-item-btn ${currentView === 'myRegistrations' ? 'active' : ''}`}
                onClick={() => setCurrentView('myRegistrations')}
              >
                <span>My Registrations</span>
                <span className="dev-nav-item-path">/my-registrations</span>
              </button>
              <button
                type="button"
                className={`dev-nav-item-btn ${currentView === 'myCertificates' ? 'active' : ''}`}
                onClick={() => setCurrentView('myCertificates')}
              >
                <span>My Certificates</span>
                <span className="dev-nav-item-path">/my-certificates</span>
              </button>
              <button
                type="button"
                className={`dev-nav-item-btn ${currentView === 'calendar' ? 'active' : ''}`}
                onClick={() => setCurrentView('calendar')}
              >
                <span>Course Calendar</span>
                <span className="dev-nav-item-path">/calendar</span>
              </button>
              {userRole === 'trainer' && (
                <>
                  <button
                    type="button"
                    className={`dev-nav-item-btn ${currentView === 'trainerCalendar' ? 'active' : ''}`}
                    onClick={() => setCurrentView('trainerCalendar')}
                  >
                    <span>Training Calendar</span>
                    <span className="dev-nav-item-path">/trainer-calendar</span>
                  </button>
                  <button
                    type="button"
                    className={`dev-nav-item-btn ${currentView === 'trainerProfile' ? 'active' : ''}`}
                    onClick={() => setCurrentView('trainerProfile')}
                  >
                    <span>Trainer Profile</span>
                    <span className="dev-nav-item-path">/trainer-profile</span>
                  </button>
                </>
              )}
              {(userRole === 'admin' || userRole === 'editor') && (
                <>
                  <button
                    type="button"
                    className={`dev-nav-item-btn ${currentView === 'adminDashboard' ? 'active' : ''}`}
                    onClick={() => setCurrentView('adminDashboard')}
                  >
                    <span>Admin Dashboard</span>
                    <span className="dev-nav-item-path">/admin</span>
                  </button>
                  <button
                    type="button"
                    className={`dev-nav-item-btn ${currentView === 'adminAccountRegistration' ? 'active' : ''}`}
                    onClick={() => setCurrentView('adminAccountRegistration')}
                  >
                    <span>Account Registration</span>
                    <span className="dev-nav-item-path">/account-registration</span>
                  </button>
                  <button
                    type="button"
                    className={`dev-nav-item-btn ${currentView === 'adminReports' ? 'active' : ''}`}
                    onClick={() => setCurrentView('adminReports')}
                  >
                    <span>Training & Class Reports</span>
                    <span className="dev-nav-item-path">/reports</span>
                  </button>
                  <button
                    type="button"
                    className={`dev-nav-item-btn ${currentView === 'adminAuditLogs' ? 'active' : ''}`}
                    onClick={() => setCurrentView('adminAuditLogs')}
                  >
                    <span>Audit Logs</span>
                    <span className="dev-nav-item-path">/audit-logs</span>
                  </button>
                  <button
                    type="button"
                    className={`dev-nav-item-btn ${currentView === 'adminSystemSettings' ? 'active' : ''}`}
                    onClick={() => setCurrentView('adminSystemSettings')}
                  >
                    <span>Application Settings</span>
                    <span className="dev-nav-item-path">/application-settings</span>
                  </button>
                  <button
                    type="button"
                    className={`dev-nav-item-btn ${currentView === 'adminFeedback' ? 'active' : ''}`}
                    onClick={() => setCurrentView('adminFeedback')}
                  >
                    <span>Feedback Management</span>
                    <span className="dev-nav-item-path">/feedback</span>
                  </button>
                  <button
                    type="button"
                    className={`dev-nav-item-btn ${currentView === 'adminRegistrationBulkImport' ? 'active' : ''}`}
                    onClick={() => setCurrentView('adminRegistrationBulkImport')}
                  >
                    <span>Import Registrations</span>
                    <span className="dev-nav-item-path">/import-registrations</span>
                  </button>
                </>
              )}
              {(userRole === 'report viewer' || userRole === 'reportviewer' || userRole === 'viewer') && (
                <button
                  type="button"
                  className={`dev-nav-item-btn ${currentView === 'adminReports' ? 'active' : ''}`}
                  onClick={() => setCurrentView('adminReports')}
                >
                  <span>Training & Class Reports</span>
                  <span className="dev-nav-item-path">/reports</span>
                </button>
              )}
              <button
                type="button"
                className={`dev-nav-item-btn ${currentView === 'dbTest' ? 'active' : ''}`}
                onClick={() => setCurrentView('dbTest')}
              >
                <span>Database Connection Test</span>
                <span className="dev-nav-item-path">/db-test</span>
              </button>
              <button
                type="button"
                className={`dev-nav-item-btn ${currentView === 'testEmail' ? 'active' : ''}`}
                onClick={() => setCurrentView('testEmail')}
              >
                <span>Test Email</span>
                <span className="dev-nav-item-path">/testemail</span>
              </button>
              <button
                type="button"
                className={`dev-nav-item-btn ${currentView === 'testReceipts' ? 'active' : ''}`}
                onClick={() => setCurrentView('testReceipts')}
              >
                <span>Receipt S3 Test</span>
                <span className="dev-nav-item-path">/testreceipts</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {currentView === 'register' ? (
        <Register 
          onSwitchView={(view, data) => {
            if (data) setPendingRegistrationData(data);
            setCurrentView(view);
          }} 
        />
      ) : (currentView === 'registrationPending' || currentView === 'registration-pending') ? (
        <RegistrationPending
          registrationData={pendingRegistrationData}
          onSwitchView={setCurrentView}
        />
      ) : currentView === 'login' ? (
        <Login
          onSwitchView={setCurrentView}
          onLoginSuccess={(userData) => {
            updateUserSession(userData);
            const r = (userData?.role || userData?.Role || '').trim().toLowerCase();
            if (r === 'admin' || r === 'editor') {
              setCurrentView('adminDashboard');
            } else if (r === 'report viewer' || r === 'reportviewer' || r === 'viewer') {
              setCurrentView('adminReports');
            } else if (r === 'trainer') {
              setCurrentView('account');
            } else {
              // Learners land on Dashboard (Course Calendar) instead of profile page
              setCurrentView('calendar');
            }
          }}
        />
      ) : currentView === 'resetPassword' ? (
        <ResetPassword onSwitchView={setCurrentView} />
      ) : (currentView === 'account' || currentView === 'trainerCalendar' || currentView === 'trainerProfile' || currentView === 'trainerDashboard' || currentView === 'trainer') ? (
        user?.role === 'Trainer' ? (
          <TrainerDashboard
            user={user}
            onLogout={() => {
              updateUserSession(null);
              setCurrentView('login');
            }}
            onSwitchView={setCurrentView}
            initialTab={
              currentView === 'trainerCalendar' ? 'calendar' :
                currentView === 'trainerProfile' ? 'profile' :
                  'overview'
            }
          />
        ) : (
          <UserAccount
            user={user}
            onLogout={() => {
              updateUserSession(null);
              setCurrentView('login');
            }}
            onUserUpdate={(updatedUser) => updateUserSession(updatedUser)}
            onSwitchView={setCurrentView}
          />
        )
      ) : (currentView === 'adminDashboard' || currentView === 'adminAccountRegistration' || currentView === 'adminAccountUpdates' || currentView === 'adminApprovals' || currentView === 'adminSessionRegistration' || currentView === 'adminAccounts' || currentView === 'adminCourses' || currentView === 'adminLearners' || currentView === 'adminCourseBulkImport' || currentView === 'adminRegistrationBulkImport' || currentView === 'adminTags' || currentView === 'adminReports' || currentView === 'adminAuditLogs' || currentView === 'adminSystemSettings' || currentView === 'adminFeedback') ? (
        <AdminDashboard
          user={user}
          onLogout={() => {
            updateUserSession(null);
            setCurrentView('login');
          }}
          onSwitchView={setCurrentView}
          activeView={currentView}
        />
      ) : currentView === 'myRegistrations' ? (
        <MyRegistrations
          user={user}
          onLogout={() => {
            updateUserSession(null);
            setCurrentView('login');
          }}
          onSwitchView={setCurrentView}
        />
      ) : currentView === 'myCertificates' ? (
        <MyCertificates
          user={user}
          onLogout={() => {
            updateUserSession(null);
            setCurrentView('login');
          }}
          onSwitchView={setCurrentView}
        />
      ) : (currentView === 'calendar' || currentView === 'dashboard') ? (
        <CourseCalendar
          user={user}
          onLogout={() => {
            updateUserSession(null);
            setCurrentView('login');
          }}
          onSwitchView={setCurrentView}
        />
      ) : currentView === 'testEmail' ? (
        <TestEmail onSwitchView={setCurrentView} />
      ) : currentView === 'testReceipts' ? (
        <TestReceiptUpload onSwitchView={setCurrentView} />
      ) : (
        <header className="App-header" style={{ minHeight: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
          <DBConnectionTest />
        </header>
      )}

      {user?.mustResetPassword && (
        <FirstLoginResetModal
          user={user}
          onPasswordResetSuccess={(res) => {
            setUser(prev => ({ ...prev, mustResetPassword: false }));
            if (res?.mustCompleteProfile || user?.mustCompleteProfile || user?.MustCompleteProfile === 1) {
              setCurrentView('account');
            }
          }}
        />
      )}

      {/* Native LMS Reusable Questionnaire and Feedback System */}
      <FloatingFeedbackButton onClick={() => setIsFeedbackModalOpen(true)} />
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        currentView={currentView}
        user={user}
        roleFilter={isAdminReportPage ? 'Admin' : 'Trainer'}
        customQuestionnaireId={isAdminReportPage ? 'admin_reports' : null}
      />
    </div>
  );
}

export default App;
